import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { X, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { ImageWithFallback } from './figma/ImageWithFallback';

export interface MetadataItem {
  id: string;
  name: string;
  displayName: string;
  imageType: 'background' | 'foreground';
  campaignTypes: string[];
  categories: string[];
  fileName: string;
}

interface MetadataUploadFormProps {
  onSubmit: (items: Omit<MetadataItem, 'id'>[]) => void;
}

export function MetadataUploadForm({ onSubmit }: MetadataUploadFormProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [displayName, setDisplayName] = useState('');
  const [imageType, setImageType] = useState<'background' | 'foreground'>('background');
  const [campaignTypes, setCampaignTypes] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setDisplayName(file.name.replace(/\.[^/.]+$/, '')); // Remove extension for default display name
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const addTag = (field: 'campaignTypes' | 'categories', value: string) => {
    if (!value.trim()) return;
    
    if (field === 'campaignTypes') {
      if (!campaignTypes.includes(value)) {
        setCampaignTypes([...campaignTypes, value]);
      }
    } else {
      if (!categories.includes(value)) {
        setCategories([...categories, value]);
      }
    }
  };

  const removeTag = (field: 'campaignTypes' | 'categories', value: string) => {
    if (field === 'campaignTypes') {
      setCampaignTypes(campaignTypes.filter(v => v !== value));
    } else {
      setCategories(categories.filter(v => v !== value));
    }
  };

  const handleSubmit = () => {
    if (!selectedFile) {
      toast.error('Please select an image');
      return;
    }

    if (!displayName.trim()) {
      toast.error('Please provide a display name');
      return;
    }

    if (campaignTypes.length === 0 || categories.length === 0) {
      toast.error('Please add at least one campaign type and category');
      return;
    }

    const newItem = {
      name: displayName.trim(), // Use displayName as the name
      displayName: displayName.trim(),
      imageType,
      campaignTypes: [...campaignTypes],
      categories: [...categories],
      fileName: selectedFile.name
    };

    onSubmit([newItem]);
    toast.success('Image uploaded successfully');

    // Reset form
    setSelectedFile(null);
    setImagePreview('');
    setDisplayName('');
    setImageType('background');
    setCampaignTypes([]);
    setCategories([]);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Upload Image
        </CardTitle>
        <CardDescription>
          Upload an image and add its metadata
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Single File Upload */}
        <div className="space-y-2">
          <Label>Select Image *</Label>
          <Input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
          />
        </div>

        {/* Preview Selected Image */}
        {selectedFile && imagePreview && (
          <div className="space-y-4">
            <Label>Image Preview</Label>
            <div className="flex gap-4 items-start border rounded-lg p-4">
              <ImageWithFallback
                src={imagePreview}
                alt={displayName}
                className="w-32 h-32 object-cover rounded flex-shrink-0"
              />
              <div className="flex-1 space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="displayName">Display Name *</Label>
                  <Input
                    id="displayName"
                    placeholder="Enter display name (e.g., Premium Black Leather Texture)"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                  />
                </div>
                <p className="text-xs text-gray-500">File: {selectedFile.name}</p>
              </div>
            </div>
          </div>
        )}

        {/* Metadata Fields */}
        {selectedFile && (
          <div className="space-y-4">
            {/* Image Type */}
            <div className="space-y-2">
              <Label htmlFor="image-type">Image Type *</Label>
              <Select
                value={imageType}
                onValueChange={(value: 'background' | 'foreground') => 
                  setImageType(value)
                }
              >
                <SelectTrigger id="image-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="background">Background</SelectItem>
                  <SelectItem value="foreground">Foreground</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Campaign Types */}
            <div className="space-y-2">
              <Label>Campaign Types *</Label>
              <Input
                placeholder="Type and press Enter to add (e.g., vehicle, incentive)"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addTag('campaignTypes', e.currentTarget.value);
                    e.currentTarget.value = '';
                  }
                }}
              />
              <div className="flex flex-wrap gap-2 mt-2">
                {campaignTypes.map(tag => (
                  <Badge key={tag} variant="secondary">
                    {tag}
                    <button
                      onClick={() => removeTag('campaignTypes', tag)}
                      className="ml-2 hover:text-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Categories */}
            <div className="space-y-2">
              <Label>Categories *</Label>
              <Input
                placeholder="Type and press Enter to add (e.g., textures, fabric)"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addTag('categories', e.currentTarget.value);
                    e.currentTarget.value = '';
                  }
                }}
              />
              <div className="flex flex-wrap gap-2 mt-2">
                {categories.map(tag => (
                  <Badge key={tag} variant="secondary">
                    {tag}
                    <button
                      onClick={() => removeTag('categories', tag)}
                      className="ml-2 hover:text-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            <Button onClick={handleSubmit} className="w-full">
              <Upload className="w-4 h-4 mr-2" />
              Upload Image
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
